import { Dispatch, FC, SetStateAction, useState } from "react";
import Checkbox from "../../../../assets/components/Checkbox/Checkbox";
import {
    Page,
    PaginationBar,
} from "../../../../assets/components/PaginationBar/PaginationBar";
import RemoveButton from "../../../../assets/components/RemoveButton/RemoveButton";
import TableHeader, {
    TableHeaderRight,
} from "../../../../assets/components/Table/TableHeader";
import {
    hasPermission,
    prepareCSVUploadData,
} from "../../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../../assets/hooks/hooks";
import { useNotification } from "../../../../assets/hooks/useNotification";
import { Permissions } from "../../../../models/models";
import { deleteAllKeysText } from "../../../../text";
import { MigratedLicenseKeysData, TotalMigratedLicense } from "../model";
import { getAllIdNotMigratedLicenses } from "../helpers";
import { migratedLicenseSliceActions } from "../../../../redux/migrated-license-slice";
import "./LicenseKeysMigratedTableHeader.scss";
import {
    useDeleteMigratedLicensesMutation,
    useUploadCSVMigratedLicenseMutation,
} from "../../../../redux/RTKQuery/api";

interface Props {
    dataTable: MigratedLicenseKeysData[];
    pageNumber: Page;
    currentSizePage: number;
    sizePage: number;
    totalLicenses: TotalMigratedLicense;
    setCurrentPage: Dispatch<SetStateAction<Page>>;
}

const LicenseKeysMigratedTableHeader: FC<Props> = ({
    sizePage,
    currentSizePage,
    dataTable,
    pageNumber,
    totalLicenses,
    setCurrentPage,
}) => {
    const dispatch = useAppDispatch();

    const [deleteLicensesHandler] = useDeleteMigratedLicensesMutation({
        fixedCacheKey: "delete_migrated_license",
    });

    const [uploadCSVfile] = useUploadCSVMigratedLicenseMutation({
        fixedCacheKey: "upload_migrated_license",
    });

    const [isRemoveAll, setRemoveAll] = useState<boolean>(false);
    const { AttentionMessageBox } = useNotification();
    const user = useAppSelector((state) => state.initializeApp);

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.LICENSE_DELETE
    );

    const candidatesForRemove = useAppSelector(
        (state) => state.migratedLicenses.candidatesForRemove
    );

    const uploadFileHandler = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const data: FormData = prepareCSVUploadData(e, false);
        uploadCSVfile(data);
        // resetting the value of Input so that you can select the same file again
        e.target.value = "";
    };

    const updateFileHandler = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const data: FormData = prepareCSVUploadData(e, true);
        uploadCSVfile(data);
        // resetting the value of Input so that you can select the same file again
        e.target.value = "";
    };

    const setAllToRemoveHandler = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        let idList: Partial<number[]> = [];
        setRemoveAll(!isRemoveAll);

        idList = getAllIdNotMigratedLicenses(dataTable);

        !isRemoveAll
            ? dispatch(migratedLicenseSliceActions.addAllCandidates(idList))
            : dispatch(migratedLicenseSliceActions.removeAllCandidates());

        return event;
    };

    const deleteAllHandler = () => {
        AttentionMessageBox({
            textContent: deleteAllKeysText(),
        }).then((value) => {
            if (value === "yes") {
                deleteLicensesHandler(candidatesForRemove);
                setRemoveAll(false);
            }
        });
    };

    return (
        <TableHeader
            pageSize={currentSizePage}
            dataTableLength={dataTable.length}
            title="List of licenses"
            onUploadFileHandler={uploadFileHandler}
            onUpdateFileHandler={updateFileHandler}
        >
            <>
                <PaginationBar
                    currentPage={pageNumber as number}
                    currentPageHandler={setCurrentPage}
                    totalItems={totalLicenses}
                    pageSize={sizePage}
                />

                <TableHeaderRight>
                    {dataTable.length && isAccessPermission ? (
                        <div className="license_keys_migrated__header_right">
                            {candidatesForRemove.length ? (
                                <RemoveButton
                                    img="center"
                                    onClick={() => deleteAllHandler()}
                                />
                            ) : null}

                            <Checkbox
                                id="remove_all"
                                name="remove_all"
                                checked={isRemoveAll}
                                value={isRemoveAll.toString()}
                                onChange={setAllToRemoveHandler}
                            />
                        </div>
                    ) : null}
                </TableHeaderRight>
            </>
        </TableHeader>
    );
};

export default LicenseKeysMigratedTableHeader;
